// src/client/components/settings/ScoreRangesTab.tsx
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@client/components/ui/card';
import { Button } from '@client/components/ui/button';
import { Badge } from '@client/components/ui/badge';
import { Database, Plus, RefreshCw, RotateCcw } from 'lucide-react';
import { useCrudMutations } from '@client/hooks/useCrudMutations';
import { CrudDialog } from '@client/components/shared/CrudDialog';
import { FormField } from '@client/components/shared/FormField';
import { DataTable, TableColumn } from '@client/components/shared/DataTable';
import type { ScoreRange, InsertScoreRange } from '@client/types/settings';

interface ScoreRangesTabProps {
  scoreRanges: ScoreRange[];
  loadScoreRanges: () => Promise<void>;
  resetScoreRangesToDefaults: () => Promise<void>;
  isLoading: boolean;
}

interface ScoreRangeFormData {
  label: string;
  minValue: number;
  maxValue: number;
  color: string;
  order: number;
}

export default function ScoreRangesTab({ 
  scoreRanges, 
  loadScoreRanges, 
  resetScoreRangesToDefaults,
  isLoading 
}: ScoreRangesTabProps) {
  const [dialogMode, setDialogMode] = useState<'create' | 'edit' | null>(null);
  const [editingRange, setEditingRange] = useState<ScoreRange | null>(null);
  const [formData, setFormData] = useState<ScoreRangeFormData>({
    label: '',
    minValue: 0,
    maxValue: 100,
    color: '#3B82F6',
    order: 0,
  });
  const [formError, setFormError] = useState<string | null>(null);

  // CRUD Mutations
  const { createMutation, updateMutation, deleteMutation } = useCrudMutations<ScoreRange>({
    endpoint: '/score-ranges',
    queryKey: ['/score-ranges'],
    entityName: 'Score Range',
  });

  // Validation
  const validateForm = (): string | null => {
    if (!formData.label.trim()) return 'Label ist erforderlich';
    if (formData.minValue >= formData.maxValue) {
      return 'Minimum muss kleiner als Maximum sein';
    }

    // Check for overlaps (excluding current range when editing)
    const hasOverlap = scoreRanges.some((range) => {
      if (editingRange && range.id === editingRange.id) return false;
      return (
        (formData.minValue >= range.minValue && formData.minValue <= range.maxValue) ||
        (formData.maxValue >= range.minValue && formData.maxValue <= range.maxValue) ||
        (formData.minValue <= range.minValue && formData.maxValue >= range.maxValue)
      );
    });

    if (hasOverlap) return 'Score range überschneidet sich mit bestehendem Range';
    return null;
  };

  // Dialog Handlers
  const openCreateDialog = () => {
    setFormData({
      label: '',
      minValue: 0,
      maxValue: 100,
      color: '#3B82F6',
      order: scoreRanges.length,
    });
    setFormError(null);
    setEditingRange(null);
    setDialogMode('create');
  };

  const openEditDialog = (range: ScoreRange) => {
    setEditingRange(range);
    setFormData({
      label: range.label,
      minValue: range.minValue,
      maxValue: range.maxValue,
      color: range.color,
      order: range.order,
    });
    setFormError(null);
    setDialogMode('edit');
  };

  const closeDialog = () => {
    setDialogMode(null);
    setEditingRange(null);
    setFormData({ label: '', minValue: 0, maxValue: 100, color: '#3B82F6', order: 0 });
    setFormError(null);
  };

  const handleSave = async () => {
    const error = validateForm();
    if (error) {
      setFormError(error);
      return;
    }

    const rangeData: InsertScoreRange = {
      label: formData.label.trim(),
      minValue: formData.minValue,
      maxValue: formData.maxValue,
      color: formData.color,
      order: formData.order || scoreRanges.length,
    };

    if (dialogMode === 'edit' && editingRange) {
      updateMutation.mutate(
        { id: editingRange.id, data: rangeData },
        {
          onSuccess: () => {
            closeDialog();
            loadScoreRanges();
          },
        }
      );
    } else {
      createMutation.mutate(rangeData, {
        onSuccess: () => {
          closeDialog();
          loadScoreRanges();
        },
      });
    }
  };

  const handleDelete = async (range: ScoreRange) => {
    deleteMutation.mutate(range.id, {
      onSuccess: () => loadScoreRanges(),
    });
  };

  // Sort by order then by minValue
  const sortedRanges = [...scoreRanges].sort((a, b) => {
    if (a.order !== b.order) return a.order - b.order;
    return a.minValue - b.minValue;
  });

  // Table Columns
  const columns: TableColumn<ScoreRange>[] = [
    {
      key: 'label',
      header: 'Label',
      render: (range) => (
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: range.color }} />
          <span className="font-medium">{range.label}</span>
        </div>
      ),
    },
    {
      key: 'range',
      header: 'Range',
      render: (range) => `${range.minValue} - ${range.maxValue}`,
    },
    {
      key: 'color',
      header: 'Color',
      render: (range) => (
        <Badge style={{ backgroundColor: range.color, color: 'white' }}>{range.color}</Badge>
      ),
    },
    {
      key: 'order',
      header: 'Order',
      render: (range) => range.order,
    },
  ];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Database className="h-4 w-4" />
            <CardTitle>Score Ranges</CardTitle>
            <Button variant="ghost" size="sm" onClick={loadScoreRanges} disabled={isLoading}>
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={resetScoreRangesToDefaults} disabled={isLoading}>
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset to Defaults
            </Button>
            <Button onClick={openCreateDialog}>
              <Plus className="h-4 w-4 mr-2" />
              Add Range
            </Button>
          </div>
        </div>
        <CardDescription>
          Define priority score ranges stored in settings. Ranges are used to categorize profile scores.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <DataTable
          data={sortedRanges}
          columns={columns}
          loading={isLoading}
          getRowKey={(range) => range.id}
          emptyState={{
            title: 'Keine Score Ranges gefunden',
            description: 'Erstellen Sie Ihren ersten Range oben oder nutzen Sie Reset to Defaults.',
          }}
          actions={{
            onEdit: openEditDialog,
            onDelete: handleDelete,
          }}
          deleteConfirmation={{
            title: 'Score Range löschen?',
            description: (range) =>
              `Sind Sie sicher, dass Sie den Score Range "${range.label}" (${range.minValue}-${range.maxValue}) löschen möchten?`,
          }}
        />

        {/* Statistics */}
        {scoreRanges.length > 0 && (
          <div className="space-y-4 pt-4 border-t">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-lg font-semibold">{scoreRanges.length}</div>
                <div className="text-sm text-muted-foreground">Total Ranges</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-semibold">
                  {Math.min(...scoreRanges.map((r) => r.minValue))} -{' '}
                  {Math.max(...scoreRanges.map((r) => r.maxValue))}
                </div>
                <div className="text-sm text-muted-foreground">Total Coverage</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-semibold">
                  {new Set(scoreRanges.map((r) => r.color)).size}
                </div>
                <div className="text-sm text-muted-foreground">Unique Colors</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-semibold">
                  {scoreRanges.reduce((acc, curr, index, arr) => {
                    const sorted = arr.sort((a, b) => a.minValue - b.minValue);
                    if (index === 0) return acc;
                    const prev = sorted[index - 1];
                    return acc + (curr.minValue - prev.maxValue - 1);
                  }, 0)}
                </div>
                <div className="text-sm text-muted-foreground">Coverage Gaps</div>
              </div>
            </div>

            {/* Visual Preview */}
            <div>
              <h4 className="text-sm font-medium mb-2">Range Visualization</h4>
              <div className="flex flex-wrap gap-2">
                {sortedRanges.map((range) => (
                  <div
                    key={range.id}
                    className="px-3 py-2 rounded text-white text-sm font-medium"
                    style={{ backgroundColor: range.color }}
                  >
                    {range.label}: {range.minValue}-{range.maxValue}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </CardContent>

      {/* CRUD Dialog */}
      <CrudDialog
        open={dialogMode !== null}
        onOpenChange={(open) => !open && closeDialog()}
        mode={dialogMode || 'create'}
        title={dialogMode === 'edit' ? 'Score Range bearbeiten' : 'Neuer Score Range'}
        description={
          dialogMode === 'edit'
            ? `Ändern Sie die Eigenschaften des Score Range "${editingRange?.label}".`
            : 'Erstellen Sie einen neuen Score Range.'
        }
        onSave={handleSave}
        onCancel={closeDialog}
        isSaving={createMutation.isPending || updateMutation.isPending}
      >
        <FormField
          id="label"
          label="Label"
          value={formData.label}
          onChange={(value) => setFormData({ ...formData, label: String(value) })}
          placeholder="z.B. High Priority, Low Priority"
          required
        />

        <div className="grid grid-cols-2 gap-4">
          <FormField
            id="minValue"
            label="Min Score"
            type="number"
            value={formData.minValue}
            onChange={(value) => setFormData({ ...formData, minValue: Number(value) || 0 })}
            required
          />

          <FormField
            id="maxValue"
            label="Max Score"
            type="number"
            value={formData.maxValue}
            onChange={(value) => setFormData({ ...formData, maxValue: Number(value) || 100 })}
            required
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <FormField
            id="color"
            label="Color"
            type="color"
            value={formData.color}
            onChange={(value) => setFormData({ ...formData, color: String(value) })}
          />

          <FormField
            id="order"
            label="Order"
            type="number"
            value={formData.order}
            onChange={(value) => setFormData({ ...formData, order: Number(value) || 0 })}
          />
        </div>

        {formError && <p className="text-sm text-red-500">{formError}</p>}
      </CrudDialog>
    </Card>
  );
}